<script setup lang="ts">
import { ref, watch } from 'vue';

import AppModal from '@/_core/shared/ui/AppModal/AppModal.vue';
import IconClose from '@/assets/images/svg/icon-close.svg?component';

const props = defineProps<{
  isOpen: boolean;
  isPending?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', comment: string): void;
  (e: 'close'): void;
}>();

const comment = ref('');
const hasError = ref(false);

function resetComment(): void {
  comment.value = '';
  hasError.value = false;
}

watch(
  () => props.isOpen,
  (open) => !open && resetComment()
);

watch(comment, () => {
  if (hasError.value) hasError.value = false;
});

function handleSubmit(): void {
  if (props.isPending) return;

  const value = comment.value.trim();

  if (!value) {
    hasError.value = true;
    return;
  }

  emit('submit', value);
  resetComment();
}

function clearAndClose(): void {
  resetComment();
  emit('close');
}
</script>

<template>
  <AppModal :is-open="isOpen" aria-labelledby="llm-feedback-comment-title" @close="clearAndClose">
    <div
      class="tw:box-border tw:flex tw:flex-col tw:items-start tw:w-[500px] tw:max-w-full tw:shrink-0 tw:p-8 tw:gap-6 tw:rounded-2xl tw:bg-white tw:font-aspekta"
    >
      <!-- header row -->
      <div class="tw:flex tw:flex-row tw:justify-between tw:items-start tw:w-full">
        <h4
          id="llm-feedback-comment-title"
          class="tw:m-0 tw:font-aspekta tw:text-[18px] tw:leading-[26px] tw:tracking-[0.03em] tw:font-semibold tw:text-main"
        >
          Tell us more (optional)
        </h4>
        <button
          type="button"
          class="tw:flex tw:items-center tw:justify-center tw:size-6 tw:shrink-0 tw:bg-transparent tw:border-0 tw:text-ghost tw:cursor-pointer tw:p-0"
          aria-label="Close modal"
          @click="clearAndClose"
        >
          <IconClose aria-hidden="true" class="tw:block tw:w-full tw:h-full" />
        </button>
      </div>

      <!-- input field -->
      <div class="tw:flex tw:flex-col tw:items-start tw:w-full tw:gap-1.5">
        <label for="feedback-textarea" class="tw:typography-small tw:text-ghost">
          What could be improved?
        </label>
        <textarea
          id="feedback-textarea"
          v-model="comment"
          class="tw:box-border tw:w-full tw:h-[114px] tw:py-[18px] tw:px-6 tw:rounded-[10px] tw:border tw:bg-white tw:text-lg tw:leading-normal tw:tracking-[0.02em] tw:text-main tw:resize-none tw:outline-none tw:transition-[border-color] tw:duration-200 disabled:tw:opacity-50 tw:font-[450]"
          :class="
            hasError ? 'tw:border-[#FF0006]' : 'tw:border-[#e3e6ed] focus:tw:border-main-blue'
          "
          :disabled="isPending"
          @keydown.ctrl.enter.prevent="handleSubmit"
        />
        <div class="tw:w-full tw:flex tw:justify-end">
          <span v-if="hasError" class="tw:typography-sidenotes tw:text-[#FF0006]">
            Comment cannot be empty
          </span>
        </div>
      </div>

      <!-- submit button -->
      <button
        type="button"
        class="tw:box-border tw:flex tw:flex-row tw:justify-center tw:items-center tw:h-[54px] tw:py-4 tw:px-6 tw:rounded-lg tw:bg-blue-bg tw:border-0 tw:typography-h2 tw:text-center tw:text-main-blue tw:cursor-pointer tw:transition-opacity tw:duration-200 hover:tw:opacity-90 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed tw:whitespace-nowrap"
        :disabled="isPending"
        @click="handleSubmit"
      >
        Send feedback
      </button>
    </div>
  </AppModal>
</template>
